// © 2025 Camp Promethean, LLC. All Rights Reserved.
// Inline Concept 1 mark — html2canvas cannot reliably rasterize external SVG <img> tags.

import { cn } from "@/lib/utils";

export const BRAND_MARK_COLOR = "#9f8ab3";

interface BrandMarkProps {
  className?: string;
  color?: string;
}

/** Purple torch/flame mark for dark backgrounds (export, sidebar, loading). */
export function BrandMark({ className, color = BRAND_MARK_COLOR }: BrandMarkProps) {
  return (
    <svg
      viewBox="0 0 254.98 277.3"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className={cn("shrink-0", className)}
    >
      <polygon
        fill={color}
        points="254.98,277.3 214.15,277.3 173.14,185.53 132.14,277.3 91.29,277.3 173.14,137.59"
      />
      <polygon fill={color} points="155.34,137.6 73.51,277.3 44.99,277.3 126.82,137.6" />
      <polygon fill={color} points="110.35,137.6 28.52,277.3 0,277.3 81.83,137.6" />
      <path
        fill={color}
        d="M160.69,53.88c-0.01-0.01-0.03-0.03-0.03-0.03c-0.38,1.12-0.8,2.26-1.25,3.39
        c-3.76,7.98-14.51,20.57-29.29,36.84c-17.07,18.79-2.22,30.11-2.22,30.11h18.31c0,0-4.39-13.85,15.63-31.05
        C181.43,76.31,161.53,54.78,160.69,53.88z"
      />
      <path
        fill={color}
        d="M110.8,119.13v-0.01c-2.37-6.55-2.5-19.51,16.73-39.52l0.04-0.04c28.91-30.1,39.22-48.9,7.11-78.55
        c-0.12-0.12-0.25-0.23-0.37-0.36c-0.16-0.15-0.34-0.31-0.52-0.48c-0.05-0.04-0.12-0.11-0.19-0.16
        c2.12,13.06-3.87,19.77-40.92,59.99c-37.05,40.21,6,64.2,6,64.2h15.16c0,0-0.01-0.01-0.04-0.03
        C113.51,123.86,111.91,122.2,110.8,119.13z"
      />
    </svg>
  );
}

/** Data URL for canvas / Image() — avoids network fetch during export. */
export function brandMarkDataUrl(color: string = BRAND_MARK_COLOR): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 254.98 277.3">
<polygon fill="${color}" points="254.98,277.3 214.15,277.3 173.14,185.53 132.14,277.3 91.29,277.3 173.14,137.59"/>
<polygon fill="${color}" points="155.34,137.6 73.51,277.3 44.99,277.3 126.82,137.6"/>
<polygon fill="${color}" points="110.35,137.6 28.52,277.3 0,277.3 81.83,137.6"/>
<path fill="${color}" d="M160.69,53.88c-0.01-0.01-0.03-0.03-0.03-0.03c-0.38,1.12-0.8,2.26-1.25,3.39c-3.76,7.98-14.51,20.57-29.29,36.84c-17.07,18.79-2.22,30.11-2.22,30.11h18.31c0,0-4.39-13.85,15.63-31.05C181.43,76.31,161.53,54.78,160.69,53.88z"/>
<path fill="${color}" d="M110.8,119.13v-0.01c-2.37-6.55-2.5-19.51,16.73-39.52l0.04-0.04c28.91-30.1,39.22-48.9,7.11-78.55c-0.12-0.12-0.25-0.23-0.37-0.36c-0.16-0.15-0.34-0.31-0.52-0.48c-0.05-0.04-0.12-0.11-0.19-0.16c2.12,13.06-3.87,19.77-40.92,59.99c-37.05,40.21,6,64.2,6,64.2h15.16c0,0-0.01-0.01-0.04-0.03C113.51,123.86,111.91,122.2,110.8,119.13z"/>
</svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}
